/**
 * TestThresholdsSection
 *
 * Manages threshold configuration with 3 modes: global, manual, automatic.
 * Mirrors portal/ui testsDetails.html THRESHOLDS section and testsDetails.controller.js
 * prepareThresholds() / prepareThresholdsForSave() logic.
 *
 * Uses UseTestThresholdsState hook for state management and virtual RHF field registration.
 * thresholdsType is managed by NgpRadioGroup Controller; thresholdsSensitivity is manually
 * registered via register() in the hook.
 */
import { t, Trans } from '@lingui/macro';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, Radio } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import HelpIcon from '@material-ui/icons/Help';
import { clamp, join, noop } from 'lodash';
import NgpRadioGroup from '../../../../form/NgpRadioGroup';
import ThresholdSensitivitySlider from '../../../thresholds/ThresholdSensitivitySlider';
import { useHelpUrl } from '../../../../../utilities/HelpUtilities';
import { usePlmPath } from '../../../../../utilities/PgmUtilities';
import useTestThresholdsState, { AUTOMATIC, GLOBAL, MANUAL } from '../hooks/UseTestThresholdsState';
import useNgpFormContext from '../../../../../utilities/NgpFormContextProviderUtilities';
import { useTestTypeContext } from '../hooks/UseTestTypeContext';
import { RowFlexLayout } from '../../../../layout';
import { useStyles } from '../../../thresholds/ThresholdsServiceTabStyles';
import ThresholdMetricRow from './ThresholdMetricRow';
import { useDetailStyles } from '../../../../panels/net-test-now-panels/NetTestNowStyles';
import LinkButton from '../../../../links/LinkButton';
import TestFormSection from './TestFormSection/TestFormSection';

const useThresholdStyles = makeStyles((theme) => ({
    thresholdCard: {
        paddingLeft: theme.spacing(4),
        paddingTop: theme.spacing(0),
        paddingBottom: theme.spacing(0),
    },
    metricName: {
        width: 250,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    metricNameManual: {
        width: 150,
        maxWidth: 150,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    metricField: {
        width: 140,
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    manageLink: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: '0.875rem',
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    helpLink: {
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: theme.spacing(1),
    },
    sliderContainer: {
        maxWidth: 460,
        paddingLeft: theme.spacing(4),
        paddingTop: theme.spacing(2),
    },
    disabledSlider: {
        maxWidth: 460,
        paddingLeft: theme.spacing(4),
        paddingTop: theme.spacing(2),
        opacity: 0.6,
        pointerEvents: 'none',
    },
    automaticRadioRow: {
        display: 'flex',
        alignItems: 'center',
    },
    // Reduce the global ngpFormFields 24px marginTop on radio labels within this section
    radioGroup: {
        marginTop: theme.spacing(2),
    },
}));

/**
 * Main Thresholds section component.
 * Three modes: Global (disabled preview), Manual (editable), Automatic (sensitivity slider).
 */
const TestThresholdsSection = ({ thresholdsRef }) => {
    const thresholdClasses = useThresholdStyles();
    const classes = useStyles();
    const styles = useDetailStyles();
    const { formData } = useNgpFormContext();
    const { testType } = useTestTypeContext();
    const autoThresholdsHelpUrl = useHelpUrl('AutomaticThresholds');
    const manageThresholdsPath = usePlmPath('/admin/thresholds');

    const {
        thresholdsType,
        thresholdsSensitivity,
        allowAutomaticThresholds,
        globalThresholds,
        hasGlobalAutomaticThreshold,
        globalAutomaticSensitivity,
        sortedFormThresholds,
        getMetricLabel,
        getMetricUnit,
        handleSensitivityChange,
        handleThresholdChange,
    } = useTestThresholdsState({ testType, formData, thresholdsRef });

    return (
        <TestFormSection title={<Trans>Thresholds</Trans>}>
            <div>
                <LinkButton label={<Trans>Manage ALL Thresholds</Trans>} newTab url={manageThresholdsPath} />
            </div>

            <div className={thresholdClasses.radioGroup}>
                <NgpRadioGroup fieldName="thresholdsType" valueType="string" className={styles.radioWrapper}>
                    {/* --- Global Mode Radio --- */}
                    <FormControlLabel value={GLOBAL} control={<Radio />} label={<Trans>Use Global Thresholds</Trans>} />
                    {thresholdsType === GLOBAL && (
                        <Box className={thresholdClasses.thresholdCard}>
                            {hasGlobalAutomaticThreshold ? (
                                <Box ml={4}>
                                    <div className={classes.sensitivityControls}>
                                        <ThresholdSensitivitySlider
                                            value={clamp(globalAutomaticSensitivity, 1, 5)}
                                            onChange={noop}
                                            containerClassName={classes.sensitivitySliderRow}
                                            labelClassName={classes.sensitivityLabelInline}
                                            sliderWrapClassName={classes.sensitivitySliderWrap}
                                        />
                                    </div>
                                </Box>
                            ) : (
                                globalThresholds.map((threshold) => {
                                    const key = join(threshold.metricIds || ['unknown'], '-');
                                    return (
                                        <ThresholdMetricRow
                                            key={`${GLOBAL}-${key}`}
                                            threshold={threshold}
                                            metricLabel={getMetricLabel(threshold.metricIds)}
                                            metricUnit={getMetricUnit(threshold.metricIds)}
                                            disabled
                                            formIndex={-1}
                                            onThresholdChange={noop}
                                            classes={thresholdClasses}
                                        />
                                    );
                                })
                            )}
                        </Box>
                    )}

                    {/* --- Manual Mode Radio --- */}
                    <FormControlLabel value={MANUAL} control={<Radio />} label={<Trans>Use Manual Thresholds</Trans>} />
                    {thresholdsType === MANUAL && (
                        <Box className={thresholdClasses.thresholdCard}>
                            {sortedFormThresholds.map((threshold) => {
                                const key = join(threshold.metricIds || ['unknown'], '-');
                                return (
                                    <ThresholdMetricRow
                                        key={`${MANUAL}-${key}`}
                                        threshold={threshold}
                                        metricLabel={getMetricLabel(threshold.metricIds)}
                                        metricUnit={getMetricUnit(threshold.metricIds)}
                                        disabled={false}
                                        formIndex={threshold.formIndex}
                                        onThresholdChange={handleThresholdChange}
                                        classes={thresholdClasses}
                                    />
                                );
                            })}
                        </Box>
                    )}

                    {/* --- Automatic Mode Radio (only visible when testType allows it) --- */}
                    {allowAutomaticThresholds && (
                        <>
                            <RowFlexLayout vBottom mt={0} className={classes.sensitivityRow}>
                                <FormControlLabel
                                    value="automatic"
                                    control={<Radio />}
                                    label={<Trans>Use Automatic Thresholds</Trans>}
                                />
                                <a
                                    href={autoThresholdsHelpUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={t`View help documentation regarding automatic thresholds`}
                                    style={{ marginBottom: 5 }}
                                >
                                    <HelpIcon fontSize="small" />
                                </a>
                            </RowFlexLayout>
                            {thresholdsType === AUTOMATIC && (
                                <Box ml={4}>
                                    <div className={classes.sensitivityControls}>
                                        <ThresholdSensitivitySlider
                                            value={clamp(thresholdsSensitivity, 1, 5)}
                                            onChange={handleSensitivityChange}
                                            containerClassName={classes.sensitivitySliderRow}
                                            labelClassName={classes.sensitivityLabelInline}
                                            sliderWrapClassName={classes.sensitivitySliderWrap}
                                        />
                                    </div>
                                </Box>
                            )}
                        </>
                    )}
                </NgpRadioGroup>
            </div>
        </TestFormSection>
    );
};

TestThresholdsSection.propTypes = {
    thresholdsRef: PropTypes.shape({ current: PropTypes.any }),
};

TestThresholdsSection.defaultProps = {
    thresholdsRef: undefined,
};

export default TestThresholdsSection;
